import { useEffect, useState } from 'react';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  RotateCcw,
  X,
  CreditCard,
  User,
  Package,
  Loader2,
} from 'lucide-react';
import type { PaymentOrderAdmin } from '../../services/api';
import { formatBeijingDateTime } from '../../utils/time';

interface OrdersTabProps {
  orders: PaymentOrderAdmin[];
  filters?: {
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalAmount: number;
    totalCredits: number;
    totalOrders: number;
  };
  loading: boolean;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onSearch: (params: {
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => void;
  onCompleteOrder: (id: string) => Promise<void>;
  onRefundOrder: (id: string, amount?: number, reason?: string) => Promise<void>;
}

const statusMap: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: '待支付', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  paid: { label: '已支付', color: 'text-green-600', bg: 'bg-green-50' },
  failed: { label: '支付失败', color: 'text-red-600', bg: 'bg-red-50' },
  refunded: { label: '已退款', color: 'text-gray-600', bg: 'bg-gray-50' },
};

const payTypeMap: Record<string, string> = {
  alipay: '支付宝',
  wxpay: '微信支付',
  qqpay: 'QQ支付',
};

function OrderDetailModal({
  order,
  onClose,
  onComplete,
  onRefund,
}: {
  order: PaymentOrderAdmin;
  onClose: () => void;
  onComplete: () => void;
  onRefund: () => void;
}) {
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [showRefundForm, setShowRefundForm] = useState(false);

  const handleRefund = () => {
    onRefund();
    setShowRefundForm(false);
    setRefundAmount('');
    setRefundReason('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-light-border">
          <div>
            <h3 className="text-lg font-semibold text-light-text">订单详情</h3>
            <p className="text-sm text-light-muted mt-1">{order.outTradeNo}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-light-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-light-muted" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 lg:p-6 space-y-4 lg:space-y-6">
          {/* 状态 */}
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${statusMap[order.status]?.bg} ${statusMap[order.status]?.color}`}>
              {statusMap[order.status]?.label || order.status}
            </span>
            {order.payType && (
              <span className="px-3 py-1 rounded-lg text-sm bg-blue-50 text-blue-600">
                {payTypeMap[order.payType] || order.payType}
              </span>
            )}
          </div>

          {/* 用户信息 */}
          <div className="bg-light-hover rounded-xl p-4">
            <h4 className="text-sm font-medium text-light-muted mb-3">用户信息</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-light-muted">用户邮箱</p>
                <p className="text-sm text-light-text font-medium break-all">{order.user.email}</p>
              </div>
              <div>
                <p className="text-xs text-light-muted">用户昵称</p>
                <p className="text-sm text-light-text font-medium">{order.user.name || '-'}</p>
              </div>
            </div>
          </div>

          {/* 订单信息 */}
          <div className="bg-light-hover rounded-xl p-4">
            <h4 className="text-sm font-medium text-light-muted mb-3">订单信息</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-light-muted">充值金额</p>
                <p className="text-lg font-bold text-light-text">¥{order.amount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-light-muted">获得积分</p>
                <p className="text-lg font-bold text-accent-blue">{order.credits}</p>
              </div>
              <div>
                <p className="text-xs text-light-muted">创建时间</p>
                <p className="text-sm text-light-text">{formatBeijingDateTime(order.createdAt)}</p>
              </div>
              {order.paidAt && (
                <div>
                  <p className="text-xs text-light-muted">支付时间</p>
                  <p className="text-sm text-light-text">{formatBeijingDateTime(order.paidAt)}</p>
                </div>
              )}
              {order.tradeNo && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-light-muted">第三方订单号</p>
                  <p className="text-sm text-light-text font-mono break-all">{order.tradeNo}</p>
                </div>
              )}
            </div>
          </div>

          {/* 退款信息 */}
          {order.status === 'refunded' && (
            <div className="bg-red-50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-red-600 mb-3">退款信息</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-red-400">退款金额</p>
                  <p className="text-sm text-red-600 font-medium">¥{order.refundAmount?.toFixed(2) || '0.00'}</p>
                </div>
                {order.refundedAt && (
                  <div>
                    <p className="text-xs text-red-400">退款时间</p>
                    <p className="text-sm text-red-600">{formatBeijingDateTime(order.refundedAt)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="p-4 border-t border-light-border bg-light-hover rounded-b-2xl">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
            {order.status === 'pending' && (
              <button
                onClick={onComplete}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                手动补单
              </button>
            )}
            {order.status === 'paid' && (
              <>
                {!showRefundForm ? (
                  <button
                    onClick={() => setShowRefundForm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    退款
                  </button>
                ) : (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <input
                      type="number"
                      placeholder="退款金额"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-light-border text-sm w-full sm:w-32"
                    />
                    <input
                      type="text"
                      placeholder="退款原因"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-light-border text-sm w-full sm:w-40"
                    />
                    <button
                      onClick={handleRefund}
                      className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors text-sm"
                    >
                      确认退款
                    </button>
                    <button
                      onClick={() => setShowRefundForm(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-600 rounded-xl hover:bg-gray-300 transition-colors text-sm"
                    >
                      取消
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrdersTab({
  orders,
  filters,
  pagination,
  stats,
  loading,
  onRefresh,
  onPageChange,
  onSearch,
  onCompleteOrder,
  onRefundOrder,
}: OrdersTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<PaymentOrderAdmin | null>(null);

  useEffect(() => {
    setSearchQuery(filters?.search || '');
    setStatusFilter(filters?.status || '');
    setStartDate(filters?.startDate || '');
    setEndDate(filters?.endDate || '');
  }, [filters?.search, filters?.status, filters?.startDate, filters?.endDate]);

  const handleSearch = () => {
    onSearch({
      search: searchQuery || undefined,
      status: statusFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    onSearch({});
  };

  const handleComplete = async () => {
    if (!selectedOrder) return;
    await onCompleteOrder(selectedOrder.id);
    setSelectedOrder(null);
    onRefresh();
  };

  const handleRefund = async () => {
    if (!selectedOrder) return;
    await onRefundOrder(selectedOrder.id);
    setSelectedOrder(null);
    onRefresh();
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">总订单数</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-2">{stats.totalOrders.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <Package className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">总金额</p>
              <p className="text-2xl lg:text-3xl font-bold text-light-text mt-2">¥{stats.totalAmount.toFixed(2)}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-green-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-light-muted text-sm">总积分</p>
              <p className="text-2xl lg:text-3xl font-bold text-accent-blue mt-2">{stats.totalCredits.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-purple-500 flex items-center justify-center">
              <User className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">搜索订单</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-muted" />
              <input
                type="text"
                placeholder="订单号 / 第三方订单号 / 用户邮箱 / 昵称 / 用户ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:flex gap-4">
            <div className="w-full lg:w-40">
              <label className="text-sm text-light-muted mb-1 block">订单状态</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              >
                <option value="">全部状态</option>
                <option value="pending">待支付</option>
                <option value="paid">已支付</option>
                <option value="failed">支付失败</option>
                <option value="refunded">已退款</option>
              </select>
            </div>
            <div className="w-full lg:w-44">
              <label className="text-sm text-light-muted mb-1 block">开始日期</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
            <div className="w-full lg:w-44">
              <label className="text-sm text-light-muted mb-1 block">结束日期</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 transition-colors flex-1 lg:flex-none"
            >
              <Search className="w-4 h-4" />
              <span>搜索</span>
            </button>
            <button
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors flex-1 lg:flex-none"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">重置</span>
            </button>
          </div>
        </div>
      </div>

      {/* 订单列表 */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border bg-light-hover">
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">订单号</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">用户</th>
                <th className="text-right text-sm font-medium text-light-muted py-3 px-4">金额</th>
                <th className="text-right text-sm font-medium text-light-muted py-3 px-4">积分</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">支付方式</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">状态</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">创建时间</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-light-muted">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>暂无订单数据</p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="border-b border-light-border last:border-0 hover:bg-light-hover/50">
                    <td className="py-3 px-4">
                      <p className="text-sm text-light-text font-mono">{order.outTradeNo}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm text-light-text">{order.user.email}</p>
                        {order.user.name && (
                          <p className="text-xs text-light-muted">{order.user.name}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="text-sm font-medium text-light-text">¥{order.amount.toFixed(2)}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <p className="text-sm font-medium text-accent-blue">{order.credits}</p>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-sm text-light-text">
                        {payTypeMap[order.payType || ''] || order.payType || '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusMap[order.status]?.bg} ${statusMap[order.status]?.color}`}>
                        {statusMap[order.status]?.label || order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-light-muted">
                        {formatBeijingDateTime(order.createdAt)}
                      </p>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 hover:bg-light-hover rounded-lg transition-colors"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4 text-light-muted" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-light-muted">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>暂无订单数据</p>
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.id} className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-light-text font-mono truncate">{order.outTradeNo}</p>
                    <p className="text-xs text-light-muted">{order.user.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-xs font-medium shrink-0 ml-2 ${statusMap[order.status]?.bg} ${statusMap[order.status]?.color}`}>
                    {statusMap[order.status]?.label || order.status}
                  </span>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">金额</p>
                    <p className="text-sm font-medium text-light-text">¥{order.amount.toFixed(2)}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">积分</p>
                    <p className="text-sm font-medium text-accent-blue">{order.credits}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">支付方式</p>
                    <p className="text-xs text-light-text">{payTypeMap[order.payType || ''] || order.payType || '-'}</p>
                  </div>
                </div>

                {/* Time & Action */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-light-muted">
                    {formatBeijingDateTime(order.createdAt)}
                  </p>
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    详情
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 border-t border-light-border gap-3">
            <p className="text-sm text-light-muted text-center sm:text-left">
              共 {pagination.total} 条记录，第 {pagination.page} / {pagination.totalPages} 页
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">上一页</span>
              </button>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">下一页</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 订单详情弹窗 */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onComplete={handleComplete}
          onRefund={handleRefund}
        />
      )}
    </div>
  );
}
